import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * Patient report draft persistence + auto-restore.
 *
 * Storage key: `medilis_patient_report_draft_v${DRAFT_VERSION}::${userId}::${studyId}`
 *
 * Lifecycle (per (userId, studyId) activation):
 *   1. Hygiene cleanup runs once: removes expired drafts (any user), older
 *      schema versions, and malformed entries. Other studies for the same
 *      user are left alone so concurrent multi-tab editing of different
 *      reports does not lose work; those drafts age out via TTL.
 *   2. If the current draft is valid (matching user/study, non-expired,
 *      non-empty), schedule auto-restore via a short timer so initial state
 *      hydration finishes first.
 *   3. Autosaves are gated until load is "resolved" (either restore fired or
 *      we determined there's nothing to restore). This guarantees we never
 *      overwrite the on-disk draft with empty initial state.
 *
 * The hook is StrictMode-safe (project is not on StrictMode, but if it ever
 * is, the gating refs only short-circuit *future* duplicate scheduling and
 * the timer is reset on dep change).
 */

const DRAFT_VERSION = 1;
const TTL_DAYS = 7;
const AUTOSAVE_DEBOUNCE_MS = 1200;
const AUTOSAVE_INTERVAL_MS = 30000;
const RESTORE_AUTO_DELAY_MS = 600;

const DRAFT_KEY_PREFIX = `medilis_patient_report_draft_v${DRAFT_VERSION}::`;
const DRAFT_LEGACY_KEY_BASE = `medilis_patient_report_draft_v`;

function nowMs() {
  return Date.now();
}

function isExpired(savedAt) {
  if (!savedAt || typeof savedAt !== "number") return true;
  const ttlMs = TTL_DAYS * 24 * 60 * 60 * 1000;
  return nowMs() - savedAt > ttlMs;
}

function safeJsonParse(raw) {
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function buildDraftKey({ userId, studyId }) {
  if (!userId || !studyId) return null;
  return `${DRAFT_KEY_PREFIX}${userId}::${studyId}`;
}

function parseDraftKey(key) {
  if (!key || !key.startsWith(DRAFT_KEY_PREFIX)) return null;
  const rest = key.slice(DRAFT_KEY_PREFIX.length);
  const idx = rest.indexOf("::");
  if (idx <= 0) return null;
  return {
    userId: rest.slice(0, idx),
    studyId: rest.slice(idx + 2),
  };
}

function buildSnapshot(payload) {
  return JSON.stringify({
    editorHtml: payload.editorHtml || "",
    sidebarTranscript: payload.sidebarTranscript || "",
    selectedTemplateId: payload.selectedTemplateId ?? null,
    templateInstructions: payload.templateInstructions || "",
  });
}

/**
 * Hygiene cleanup of `localStorage` draft entries. Removes only:
 *   - older schema versions (medilis_patient_report_draft_v* != current)
 *   - expired payloads (any user, TTL exceeded)
 *   - malformed/unparsable payloads
 *
 * Deliberately does NOT remove drafts for the current user under a different
 * studyId. Each tab/study keeps its own draft so concurrent multi-tab editing
 * (same user, different reports) does not lose work; old drafts age out
 * naturally via TTL.
 */
function cleanupStaleDrafts() {
  let storage;
  try {
    storage = window.localStorage;
  } catch (_) {
    return;
  }
  if (!storage) return;

  const toRemove = [];
  let length = 0;
  try {
    length = storage.length;
  } catch (_) {
    return;
  }

  for (let i = 0; i < length; i++) {
    let key = null;
    try {
      key = storage.key(i);
    } catch (_) {
      continue;
    }
    if (!key) continue;

    if (
      key.startsWith(DRAFT_LEGACY_KEY_BASE) &&
      !key.startsWith(DRAFT_KEY_PREFIX)
    ) {
      toRemove.push(key);
      continue;
    }

    const parsedKey = parseDraftKey(key);
    if (!parsedKey) continue;

    let raw = null;
    try {
      raw = storage.getItem(key);
    } catch (_) {
      raw = null;
    }
    const parsedPayload = safeJsonParse(raw);
    if (!parsedPayload || parsedPayload.v !== DRAFT_VERSION) {
      toRemove.push(key);
      continue;
    }
    if (isExpired(parsedPayload.savedAt)) {
      toRemove.push(key);
    }
  }

  for (const key of toRemove) {
    try {
      storage.removeItem(key);
    } catch (_) {
      // ignore
    }
  }
}

export default function usePatientReportDraft({
  enabled,
  userId,
  studyId,
  transcriptHtml,
  sidebarTranscript,
  selectedTemplateId,
  templateInstructions,
  onRestore,
}) {
  const draftKey = useMemo(
    () => buildDraftKey({ userId, studyId }),
    [userId, studyId],
  );

  // ---- Refs --------------------------------------------------------------
  // Latest `onRestore` callback. Using a ref keeps inline arrows passed by
  // the parent from re-running effects on every parent render.
  const onRestoreRef = useRef(onRestore);
  useEffect(() => {
    onRestoreRef.current = onRestore;
  }, [onRestore]);

  // Pending auto-restore timer + the draftKey it was scheduled for.
  const restoreTimerRef = useRef(null);
  const restoreTimerKeyRef = useRef(null);

  // Set the moment we decide what to do with the load (restore scheduled OR
  // skipped). Prevents the load effect from re-deciding on the same key.
  const restoreScheduledForKeyRef = useRef(null);

  // Set when load is fully resolved: either restore actually fired, or we
  // determined there's nothing to restore. Gates *all* save paths so an
  // autosave can never overwrite the on-disk draft with empty initial state.
  const loadResolvedForKeyRef = useRef(null);

  // Once-per-(userId, studyId) stale-draft cleanup guard.
  const cleanupRanForKeyRef = useRef(null);

  // Snapshot of the last successful save, used for change detection.
  const lastSavedSnapshotRef = useRef("");
  const dirtyRef = useRef(false);
  const debounceTimerRef = useRef(null);

  // ---- Helpers -----------------------------------------------------------
  const clearRestoreTimer = useCallback(() => {
    if (restoreTimerRef.current) {
      clearTimeout(restoreTimerRef.current);
      restoreTimerRef.current = null;
    }
    restoreTimerKeyRef.current = null;
  }, []);

  const buildPayload = useCallback(() => {
    return {
      v: DRAFT_VERSION,
      savedAt: nowMs(),
      userId,
      studyId,
      editorHtml: transcriptHtml || "",
      sidebarTranscript: sidebarTranscript || "",
      selectedTemplateId: selectedTemplateId ?? null,
      templateInstructions: templateInstructions || "",
    };
  }, [
    userId,
    studyId,
    transcriptHtml,
    sidebarTranscript,
    selectedTemplateId,
    templateInstructions,
  ]);

  const removeDraft = useCallback(() => {
    if (!draftKey) return;
    try {
      localStorage.removeItem(draftKey);
    } catch (_) {
      // ignore
    }
    clearRestoreTimer();
    restoreScheduledForKeyRef.current = draftKey;
    // Keep load "resolved" so any further autosave path is allowed to write
    // a fresh recovery draft if the user keeps editing after save.
    loadResolvedForKeyRef.current = draftKey;
    lastSavedSnapshotRef.current = "";
    dirtyRef.current = false;
  }, [draftKey, clearRestoreTimer]);

  const saveDraft = useCallback(() => {
    if (!enabled || !draftKey) return;
    // Hard guard: never write before the load decision has been made.
    if (loadResolvedForKeyRef.current !== draftKey) return;

    const payload = buildPayload();
    const snapshot = buildSnapshot(payload);

    if (snapshot === lastSavedSnapshotRef.current) {
      dirtyRef.current = false;
      return;
    }

    try {
      localStorage.setItem(draftKey, JSON.stringify(payload));
      lastSavedSnapshotRef.current = snapshot;
      dirtyRef.current = false;
    } catch (_) {
      // Storage full / blocked / private mode - fail silently.
    }
  }, [enabled, draftKey, buildPayload]);

  // ---- Effects -----------------------------------------------------------

  // (1) Hygiene cleanup on activation. Runs once per (userId, studyId).
  // Removes only expired, malformed, or older-schema entries. Other studies
  // for the same user are intentionally left alone so concurrent multi-tab
  // editing does not lose work.
  useEffect(() => {
    if (!enabled || !userId || !studyId) return;
    const cacheKey = `${userId}::${studyId}`;
    if (cleanupRanForKeyRef.current === cacheKey) return;
    cleanupRanForKeyRef.current = cacheKey;
    cleanupStaleDrafts();
  }, [enabled, userId, studyId]);

  // (2) Auto-restore.
  // Deps are intentionally minimal & stable so the effect runs once per
  // (userId, studyId) activation. `onRestore` is read via a ref to avoid
  // identity-induced churn from inline arrows in the parent.
  useEffect(() => {
    if (!enabled || !draftKey) return;

    // If a stale timer was scheduled for a *different* key (study change),
    // cancel it so we don't restore old content into a new study's editor.
    if (
      restoreTimerKeyRef.current &&
      restoreTimerKeyRef.current !== draftKey
    ) {
      clearRestoreTimer();
    }

    // Already decided what to do with this key (timer scheduled OR skipped).
    if (restoreScheduledForKeyRef.current === draftKey) return;

    let raw = null;
    try {
      raw = localStorage.getItem(draftKey);
    } catch (_) {
      raw = null;
    }

    const markResolved = () => {
      restoreScheduledForKeyRef.current = draftKey;
      loadResolvedForKeyRef.current = draftKey;
    };

    if (!raw) {
      markResolved();
      return;
    }

    const parsed = safeJsonParse(raw);
    if (!parsed || parsed.v !== DRAFT_VERSION) {
      try {
        localStorage.removeItem(draftKey);
      } catch (_) {
        // ignore
      }
      markResolved();
      return;
    }

    if (isExpired(parsed.savedAt)) {
      try {
        localStorage.removeItem(draftKey);
      } catch (_) {
        // ignore
      }
      markResolved();
      return;
    }

    // Defense-in-depth: even though the key is scoped, only restore when the
    // payload matches the current user/study. On mismatch, ignore (do NOT
    // delete - the legitimate session for that user/study should still find
    // its own draft).
    if (parsed.userId !== userId || parsed.studyId !== studyId) {
      markResolved();
      return;
    }

    const hasUsefulContent =
      (parsed.editorHtml && String(parsed.editorHtml).trim()) ||
      (parsed.sidebarTranscript && String(parsed.sidebarTranscript).trim()) ||
      parsed.selectedTemplateId ||
      (parsed.templateInstructions &&
        String(parsed.templateInstructions).trim());

    if (!hasUsefulContent) {
      markResolved();
      return;
    }

    // Mark "scheduled" *before* setTimeout so an effect re-run with the same
    // draftKey short-circuits and does not duplicate the timer. Note we do
    // NOT mark "loadResolved" yet - that flips only after the timer fires.
    restoreScheduledForKeyRef.current = draftKey;
    restoreTimerKeyRef.current = draftKey;
    restoreTimerRef.current = setTimeout(() => {
      try {
        onRestoreRef.current?.(parsed);
        // Prime the autosave snapshot with what we just restored so the
        // very next autosave does not perform a redundant rewrite.
        lastSavedSnapshotRef.current = buildSnapshot(parsed);
      } finally {
        restoreTimerRef.current = null;
        restoreTimerKeyRef.current = null;
        loadResolvedForKeyRef.current = draftKey;
      }
    }, RESTORE_AUTO_DELAY_MS);

    return () => {
      // Only clear if the timer is still tied to *this* draftKey. Effect
      // re-runs with the same key won't reach here (early-returned above);
      // genuine dep changes (e.g. study switch) fall through and the next
      // run will reschedule (or skip) for the new key.
      if (restoreTimerKeyRef.current === draftKey) {
        clearRestoreTimer();
      }
    };
  }, [enabled, draftKey, userId, studyId, clearRestoreTimer]);

  // (3) Debounced autosave on changes.
  useEffect(() => {
    if (!enabled || !draftKey) return;
    dirtyRef.current = true;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      saveDraft();
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [
    enabled,
    draftKey,
    transcriptHtml,
    sidebarTranscript,
    selectedTemplateId,
    templateInstructions,
    saveDraft,
  ]);

  // (4) Interval fallback autosave.
  useEffect(() => {
    if (!enabled || !draftKey) return;
    const id = setInterval(() => {
      if (dirtyRef.current) saveDraft();
    }, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enabled, draftKey, saveDraft]);

  // (5) beforeunload flush. saveDraft is internally gated by loadResolved.
  useEffect(() => {
    if (!enabled || !draftKey) return;
    const handler = () => {
      if (dirtyRef.current) saveDraft();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [enabled, draftKey, saveDraft]);

  return {
    draftKey,
    saveDraft,
    removeDraft,
  };
}
